import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActiveGame,
  selectActivePokemon,
  selectBench,
  selectDeckPile,
  selectGameIsStarted,
  selectGamePlayer,
  selectGameTurn,
  selectIsActivePlayer,
  selectIsMainPhase,
  selectOpponent,
  selectOptions,
} from '../ActiveGame/selectors';
import { setContextMenu } from '../ActiveGame/actions';
import { ACTIVE } from '../ActiveGame/contextMenuTypes';
import { ATTACH_TARGET, BENCH_SPOT, EVOLVE_TARGET, PICK_TARGET, selectBoardChoice, SWITCH_IN } from '../Board/selectors';
import { attachCancel, addToBenchCancel, evolveCancel } from '../Pokemon/actions';
import { retreatDone } from '../RetreatButton/actions';
import { picksCancel } from '../Picks/actions';
import { selectPicksSource } from '../Picks/selectors';
import SetDeck from '../ActiveGame/SetDeck';
import GameStart from '../GameStart';
import GameOver from '../GameOver';
import DrawButton from '../DrawButton';
import PassButton from '../PassButton';
import { Button, ButtonRow } from '../Controls';
import { turnStatus } from './turnStatus';
import CoinFlips from './CoinFlips';
import RevealedHands from './RevealedHands';
import { useHeightHeldWhileDragging } from '../Drag/dropTargets';
import { Banner, Hint, Title } from './styled';

const cancelChoice = {
  [ATTACH_TARGET]: attachCancel,
  [BENCH_SPOT]: addToBenchCancel,
  [SWITCH_IN]: retreatDone,
  [EVOLVE_TARGET]: evolveCancel,
  [PICK_TARGET]: picksCancel,
};

/** Where the game stands, what to do next, and the buttons for it. */
const TurnBanner = ({ userId }) => {
  const dispatch = useDispatch();
  const game = useSelector(selectActiveGame);
  const activePokemon = useSelector((state) => selectActivePokemon(state, { userId }));
  const bench = useSelector((state) => selectBench(state, { userId }));
  const boardChoice = useSelector(selectBoardChoice);
  const deckPile = useSelector((state) => selectDeckPile(state, { userId }));
  const gameIsStarted = useSelector(selectGameIsStarted);
  const gamePlayer = useSelector((state) => selectGamePlayer(state, { userId }));
  const gameTurn = useSelector(selectGameTurn);
  const isMyTurn = useSelector((state) => selectIsActivePlayer(state, { userId }));
  const isMainPhase = useSelector((state) => selectIsMainPhase(state, { userId }));
  const opponent = useSelector(selectOpponent);
  const options = useSelector(selectOptions);
  const picksSource = useSelector(selectPicksSource);
  const [bannerRef, heldWhileDragging] = useHeightHeldWhileDragging();

  if (game.get('ended_at')) {
    return <Banner><GameOver game={game} userId={userId} /></Banner>;
  }

  const status = turnStatus({
    boardChoice,
    hasDeck: !!deckPile,
    started: gameIsStarted,
    isMyTurn,
    drew: !!(gameTurn && gameTurn.get('drew')),
    attackStarted: !!(gameTurn && gameTurn.get('attack_started')),
    prizesToTake: gamePlayer ? gamePlayer.get('prizes') : 0,
    hasActive: !!activePokemon,
    hasBench: !!bench && bench.get('cards').size > 0,
    opponentHasActive: !!opponent.get('activePokemon'),
    switchInOwed: !!options && options.get('switch_in') === true,
    defenderSwitchOwed: !!(gameTurn && gameTurn.get('defender_switch_owed')),
    suddenDeathRound: game.get('sudden_death_round') || 0,
    looking: gameTurn && gameTurn.get('looking') ? gameTurn.getIn(['looking', 'label']) : null,
    picking: picksSource ? picksSource.get('name') : null,
  });

  let actions = null;

  if (boardChoice) {
    actions = <Button onClick={() => dispatch(cancelChoice[boardChoice]())}>Cancel</Button>;
  } else if (!deckPile) {
    actions = <SetDeck userId={userId} />;
  } else if (!gameIsStarted) {
    actions = <GameStart userId={userId} />;
  } else if (isMyTurn) {
    actions = (
      <>
        <DrawButton userId={userId} />
        {isMainPhase && activePokemon && <Button $primary onClick={() => dispatch(setContextMenu(ACTIVE, userId))}>
          Attack
        </Button>}
        <PassButton userId={userId} />
      </>
    );
  }

  return (
    <Banner ref={bannerRef} style={heldWhileDragging} $highlight={isMyTurn || !!boardChoice || status.highlight}>
      <div>
        <Title>{status.title}</Title>
        <Hint>{status.hint}</Hint>
      </div>
      {actions && <ButtonRow>{actions}</ButtonRow>}
      <CoinFlips />
      <RevealedHands />
    </Banner>
  );
};

TurnBanner.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default TurnBanner;
